import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card,
  CardContent, 
  Button, 
  Alert,
  Container,
  Stack,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  PlayArrow,
  Pause,
  VolumeUp,
  LocalOffer,
  CheckCircle,
  Star
} from '@mui/icons-material';
import { testimonials } from '../../utils/testimonials';
import { supabase } from '../../config/supabase';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../hooks/useAuth';
import { ttsService } from '../../services/ttsService';
import { loadStripe } from '@stripe/stripe-js';
import AdvancedTherapyRAG from '../../services/advancedTherapyRAG';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Helper functions for insights generation
const formatQuizForRAG = (answers) => {
  return answers.map(answer => {
    const questionTexts = {
      1: 'What brings you to wellness coaching today',
      2: 'How long have you been experiencing these challenges',
      3: 'What is your preferred counseling style',
      4: 'How would you rate your current stress level',
      5: 'What time of day do you typically feel most motivated',
      6: 'Which wellness areas are you most interested in improving',
      7: 'Personal story and details'
    };
    return `${questionTexts[answer.question_id]}: ${Array.isArray(answer.answer) ? answer.answer.join(', ') : answer.answer}`;
  }).join('. ');
};

const getConcernSpecificMethod = (concern) => {
  const methodMap = {
    'Anxiety': 'Cognitive Behavioral Therapy for anxiety management',
    'Depression': 'Behavioral activation and mood enhancement techniques',
    'Relationships': 'Communication skills and attachment-based therapy',
    'Personal Growth': 'Goal-setting and mindfulness-based development',
    'Other': 'Integrative therapeutic approaches'
  };
  return methodMap[concern] || methodMap['Other'];
};

const getStyleSpecificMethod = (style) => {
  const styleMap = {
    'Direct and structured': 'Solution-focused brief therapy',
    'Gentle and supportive': 'Person-centered therapeutic approach',
    'Mix of both': 'Integrative therapy combining multiple modalities',
    'Not sure': 'Exploratory therapy to find your best fit'
  };
  return styleMap[style] || styleMap['Not sure'];
};

const getStressSpecificMethod = (stress) => {
  if (stress.includes('Very high') || stress.includes('High')) {
    return 'Stress reduction and crisis intervention techniques';
  } else if (stress.includes('Moderate')) {
    return 'Mindfulness and coping skills development';
  } else {
    return 'Preventive wellness and resilience building';
  }
};

const getWellnessApproach = (area, primaryConcern, style) => {
  const approaches = {
    'Stress management and relaxation': {
      'Anxiety': 'Mindfulness-based stress reduction with breathing techniques',
      'Depression': 'Progressive muscle relaxation and guided imagery',
      'Relationships': 'Stress inoculation training for relationship challenges',
      'Personal Growth': 'Meditation and self-awareness practices'
    },
    'Sleep quality and routine': {
      'Anxiety': 'Cognitive-behavioral therapy for insomnia (CBT-I)',
      'Depression': 'Sleep hygiene and circadian rhythm regulation',
      'Relationships': 'Couples sleep counseling and boundary setting',
      'Personal Growth': 'Sleep optimization for peak performance'
    },
    'Emotional regulation': {
      'Anxiety': 'Dialectical behavior therapy (DBT) skills',
      'Depression': 'Emotion-focused therapy techniques',
      'Relationships': 'Attachment-based emotional regulation',
      'Personal Growth': 'Mindfulness-based emotional intelligence'
    },
    'Communication skills': {
      'Anxiety': 'Assertiveness training and social anxiety management',
      'Depression': 'Interpersonal therapy communication skills',
      'Relationships': 'Gottman Method couples communication',
      'Personal Growth': 'Nonviolent communication and leadership skills'
    },
    'Self-confidence and self-esteem': {
      'Anxiety': 'Cognitive restructuring and self-compassion',
      'Depression': 'Behavioral activation and achievement therapy',
      'Relationships': 'Attachment security and boundary work',
      'Personal Growth': 'Strengths-based positive psychology'
    },
    'Work-life balance': {
      'Anxiety': 'Stress management and boundary setting',
      'Depression': 'Behavioral activation and life structure',
      'Relationships': 'Family systems and role negotiation',
      'Personal Growth': 'Values clarification and goal alignment'
    },
    'Mindfulness and meditation': {
      'Anxiety': 'Mindfulness-based anxiety reduction (MBAR)',
      'Depression': 'Mindfulness-based cognitive therapy (MBCT)',
      'Relationships': 'Mindful relationship practices',
      'Personal Growth': 'Advanced contemplative practices'
    },
    'Goal setting and achievement': {
      'Anxiety': 'Graduated exposure and achievement therapy',
      'Depression': 'Behavioral activation and goal pursuit',
      'Relationships': 'Couples goal-setting and shared vision',
      'Personal Growth': 'Life coaching and purpose-driven planning'
    }
  };

  return approaches[area]?.[primaryConcern] || 
         approaches[area]?.['Personal Growth'] || 
         `Personalized ${area.toLowerCase()} approach`;
};

const generateStructuredInsights = async (answers, ragAnalysis) => {
  const primaryConcern = answers.find(a => a.question_id === 1)?.answer || 'wellness';
  const duration = answers.find(a => a.question_id === 2)?.answer || 'some time';
  const style = answers.find(a => a.question_id === 3)?.answer || 'supportive';
  const stressLevel = answers.find(a => a.question_id === 4)?.answer || 'moderate';
  const motivation = answers.find(a => a.question_id === 5)?.answer || 'varies';
  const wellnessAreas = answers.find(a => a.question_id === 6)?.answer || [];
  const personalStory = answers.find(a => a.question_id === 7)?.answer || '';

  // Use enhanced RAG analysis
  const selectedMethods = ragAnalysis?.methods?.map(method => method.methodName || method.id) || [
    getConcernSpecificMethod(primaryConcern),
    getStyleSpecificMethod(style),
    getStressSpecificMethod(stressLevel)
  ].filter(Boolean);

  // Create personalized wellness plan based on selected areas
  const wellnessAreasArray = Array.isArray(wellnessAreas) ? wellnessAreas : [wellnessAreas];
  const wellnessPlan = wellnessAreasArray.slice(0, 3).map(area => ({
    area: area,
    approach: getWellnessApproach(area, primaryConcern, style)
  }));

  return {
    personalizedMessage: personalStory 
      ? `Based on your personal story and focus on ${primaryConcern.toLowerCase()}, we've created a customized wellness plan that addresses your unique situation. Having experienced these challenges for ${duration.toLowerCase()}, you're ready for the personalized, ${style.toLowerCase()} therapeutic support that will create lasting change.`
      : `Your ${primaryConcern.toLowerCase()} journey deserves expert guidance. Having experienced these challenges for ${duration.toLowerCase()}, you're ready for the breakthrough that comes with personalized, ${style.toLowerCase()} therapeutic support.`,
    
    keyInsights: [
      {
        title: 'Your Unique Profile',
        content: `Your preference for ${style.toLowerCase()} guidance combined with your ${stressLevel.toLowerCase()} stress levels indicates you would benefit most from our adaptive wellness approach that meets you exactly where you are.`
      },
      {
        title: 'Personalized Growth Plan',
        content: `Based on your focus areas (${wellnessAreasArray.slice(0, 2).join(' and ')}), we've identified specific therapeutic approaches that align with your goals and ${primaryConcern.toLowerCase()} concerns.`
      },
      {
        title: 'Optimal Timing & Readiness',
        content: `Your ${motivation.toLowerCase()} motivation pattern and detailed personal insights suggest you're at the perfect time to begin this transformative journey with maximum therapeutic benefit.`
      }
    ],
    
    therapeuticRecommendations: selectedMethods.slice(0, 3),
    personalizedWellnessPlan: wellnessPlan,
    
    successPredictors: [
      'Evidence-based methods tailored to your specific needs',
      '24/7 AI support that understands your unique situation',
      'Proven track record with similar client profiles',
      'Flexible approach that adapts to your progress',
      `Specialized focus on ${wellnessAreasArray.slice(0, 2).join(' and ')}`
    ],
    
    urgencyFactors: [
      `You've been managing ${primaryConcern.toLowerCase()} for ${duration.toLowerCase()} - intervention now can prevent escalation`,
      'Early therapeutic intervention shows significantly better outcomes',
      'Your motivation and awareness indicate optimal readiness for change',
      personalStory ? 'Your detailed personal story shows deep self-awareness - a key predictor of therapeutic success' : ''
    ].filter(Boolean)
  };
};

const TestimonialBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: 'rgba(52, 152, 219, 0.05)',
  position: 'relative'
}));

const QuoteText = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  lineHeight: 1.6,
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(6),
  position: 'relative',
  '&::before': {
    content: '"\\201C"',
    position: 'absolute',
    left: theme.spacing(1),
    top: theme.spacing(-2),
    fontSize: '4rem',
    color: 'rgba(52, 152, 219, 0.2)',
    fontFamily: 'Georgia, serif',
  }
}));

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth(); // session from useAuth might be useful for conditional logic
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [signupData, setSignupData] = useState(null); // This will hold 'completedQuizData'
  const [personalizedInsights, setPersonalizedInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioSupported, setAudioSupported] = useState(true);
  const [preGeneratedAudio, setPreGeneratedAudio] = useState(null); // Cache for pre-generated audio

  const [insightsLoading, setInsightsLoading] = useState(false);
  const [generatedInsights, setGeneratedInsights] = useState(null);

  // Select a random testimonial
  const testimonial = useMemo(() => 
    testimonials[Math.floor(Math.random() * testimonials.length)],
    []
  );

  // Initialize TTS service callbacks
  useEffect(() => {
    ttsService.setCallbacks(
      () => {
        setIsLoadingAudio(false);
        setIsPlaying(true);
      },
      () => {
        setIsPlaying(false);
        setIsLoadingAudio(false);
      }
    );
  }, []);

  // TTS functions for personalized insights
  const preGenerateInsightsAudio = async (insights) => {
    if (!insights || !audioSupported) return null;

    // Create a natural audio script for the subscription page insights
    const audioScript = `
      Here's your personalized therapy plan.
      ${insights.personalizedMessage}
      
      ${insights.keyInsights?.slice(0, 2).map(insight => 
        `${insight.title}. ${insight.content}`
      ).join('. ')}
      
      ${insights.personalizedWellnessPlan && insights.personalizedWellnessPlan.length > 0 ? 
        `Your personalized wellness plan focuses on ${insights.personalizedWellnessPlan.map(plan => plan.area).slice(0, 2).join(' and ')}.` : 
        ''}
      
      ${insights.urgencyFactors?.[0] || ''}
      
      This personalized approach is designed specifically for you. Take advantage of this opportunity to start your transformation today.
    `.replace(/\s+/g, ' ').trim();

    console.log('� Audio script generated:', audioScript.substring(0, 100) + '...');

    try {
      console.log('🎵 Pre-generating audio for subscription insights...');
      // Generate audio blob but don't play it yet
      const audioBlob = await ttsService.generateAudioBlob(audioScript, 'female', 'nova');
      
      console.log('✅ Audio pre-generated successfully');
      return audioBlob;
    } catch (error) {
      console.error('Error pre-generating subscription insights audio:', error);
      return null;
    }
  };

  const playInsightsAudio = async () => {
    if (!personalizedInsights || isPlaying || isLoadingAudio) return;

    try {
      setIsLoadingAudio(true);
      
      // Use pre-generated audio if available
      if (preGeneratedAudio) {
        console.log('🚀 Using pre-generated audio - instant playback!');
        await ttsService.playAudioBlob(preGeneratedAudio);
      } else {
        console.log('⏳ No pre-generated audio, generating on-demand...');
        // Fallback to generating on-demand (existing behavior)
        const audioScript = `
          Here's your personalized therapy plan.
          ${personalizedInsights.personalizedMessage}
          
          ${personalizedInsights.keyInsights?.slice(0, 2).map(insight => 
            `${insight.title}. ${insight.content}`
          ).join('. ')}
          
          ${personalizedInsights.personalizedWellnessPlan && personalizedInsights.personalizedWellnessPlan.length > 0 ? 
            `Your personalized wellness plan focuses on ${personalizedInsights.personalizedWellnessPlan.map(plan => plan.area).slice(0, 2).join(' and ')}.` : 
            ''}
          
          ${personalizedInsights.urgencyFactors?.[0] || ''}
          
          This personalized approach is designed specifically for you. Take advantage of this opportunity to start your transformation today.
        `.replace(/\s+/g, ' ').trim();

        await ttsService.speakText(audioScript, { 
          gender: 'female',
          voiceId: 'nova' // OpenAI female voice
        });
      }
    } catch (error) {
      console.error('Error playing subscription insights audio:', error);
      setAudioSupported(false);
      setIsPlaying(false);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const stopAudio = () => {
    ttsService.stop();
    setIsPlaying(false);
    setIsLoadingAudio(false);
  };

  useEffect(() => {
    // Get completed quiz data from session storage if it exists
    const storedCompletedQuizData = sessionStorage.getItem('completedQuizData');
    console.log('🔍 Checking sessionStorage for quiz data:', {
      hasStoredData: !!storedCompletedQuizData,
      rawData: storedCompletedQuizData?.substring(0, 100) + '...'
    });
    
    if (storedCompletedQuizData) {
      try {
        const parsedData = JSON.parse(storedCompletedQuizData);
        console.log('✅ Successfully parsed completedQuizData:', {
          hasEmail: !!parsedData.email,
          hasQuizAnswers: !!parsedData.quizAnswers,
          hasCounselingType: !!parsedData.counseling_type,
          quizAnswersLength: parsedData.quizAnswers?.length,
          counselingType: parsedData.counseling_type
        });
        setSignupData(parsedData); // This state now holds 'completedQuizData'
      } catch (e) {
        console.error("❌ Error parsing completedQuizData from sessionStorage:", e);
        sessionStorage.removeItem('completedQuizData'); // Clear corrupted data
      }
    } else {
      console.warn('⚠️  No completedQuizData found in sessionStorage');
    }

    // Get personalized insights from location state
    if (location.state?.personalizedInsights) {
      setPersonalizedInsights(location.state.personalizedInsights);
      
      // Pre-generate audio for insights from location state
      preGenerateInsightsAudio(location.state.personalizedInsights).then(audioBlob => {
        setPreGeneratedAudio(audioBlob);
      }).catch(error => {
        console.error('Error pre-generating audio from location state:', error);
      });
    }

    // Check for messages (e.g., subscription expired) in location state
    if (location.state?.message) {
      setMessage(location.state.message);
    }
  }, [location.state]);

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('user_id', session.user.id)
            .single();
          
          if (profile) {
            setUserProfile(profile);
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, []);



  const handleSubscribe = async (plan) => {
    try {
      setLoading(true);
      setError(null);
      setMessage(null); 

      // User must be authenticated (session exists) or have signupData (from quiz completion)
      // to proceed to payment. The `signupData` state variable holds `completedQuizData`.
      if (!session && !signupData) {
        console.warn("No session or signupData, redirecting to login.");
        navigate('/login');
        setLoading(false);
        return;
      }
      
      if (!signupData) {
        console.warn("⚠️  No signupData found in sessionStorage, checking database...");
        
        // Fallback: try to get quiz data from database
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.user?.id) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('email, counseling_type, quiz_answers')
            .eq('id', currentSession.user.id)
            .single();
          
          if (profile && profile.quiz_answers) {
            console.log('✅ Found quiz data in database, using as fallback');
            setSignupData({
              email: profile.email,
              counseling_type: profile.counseling_type,
              quizAnswers: profile.quiz_answers
            });
          } else {
            console.error("❌ No quiz data found in database either");
            setError("Please complete the wellness assessment before subscribing.");
            setLoading(false);
            return;
          }
        } else {
          console.error("❌ No session found to fetch quiz data");
          setError("Please complete the wellness assessment before subscribing.");
          setLoading(false);
          return;
        }
      }

      console.log('📋 SignupData being sent to checkout:', {
        hasSignupData: !!signupData,
        hasQuizAnswers: !!signupData?.quizAnswers,
        hasCounselingType: !!signupData?.counseling_type,
        hasEmail: !!signupData?.email,
        dataKeys: Object.keys(signupData || {}),
        signupDataPreview: signupData
      });

      // Get the current user session for proper authorization
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession) {
        throw new Error('No active session found');
      }

      console.log('Session check:', {
        hasSession: !!currentSession,
        hasAccessToken: !!currentSession.access_token,
        tokenLength: currentSession.access_token?.length
      });

      // Create checkout session using Supabase Function
      // Pass the component's signupData state which contains the completedQuizData
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentSession.access_token}`, 
          },
          body: JSON.stringify({
            plan,
            signupData: signupData 
          })
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (!data.url) {
        throw new Error('No checkout URL received');
      }

      // Store subscription type in sessionStorage for PaymentSuccess component
      sessionStorage.setItem('selectedSubscriptionType', plan.toLowerCase());
      console.log('💾 Stored subscription type in sessionStorage:', plan.toLowerCase());

      // Redirect to Stripe checkout
      window.location.href = data.url;

    } catch (err) { // Changed error variable name to avoid conflict
      console.error('Error creating checkout session:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      name: 'Yearly',
      price: '$120/year',
      originalPrice: '$179.88',
      savings: 'Save $59.88',
      dailyPrice: '$0.32/day',
      features: [
        'Best value - only $0.32/day',
        'Unlimited AI wellness sessions',
        'Voice interactions with premium voices',
        'Advanced progress tracking & insights',
        'Priority support & early access',
        'Save 33% compared to monthly',
        '24/7 AI availability',
        'Personalized therapy plans',
        'Monthly wellness reports'
      ],
      recommended: true
    },
    {
      name: 'Monthly',
      price: '$14.99/month',
      dailyPrice: '$0.49/day',
      features: [
        'Unlimited AI wellness sessions',
        'Voice interactions',
        'Basic progress tracking',
        'Cancel anytime'
      ]
    }
  ];

  // Skip teaser and go directly to subscription plans
  // if (showTeaser && signupData?.teaserSummary) {
  //   return (
  //     <Container maxWidth="lg">
  //       <Box sx={{ mt: 4, p: 2 }}>
  //         {error && (
  //           <Alert severity="error" sx={{ mb: 3 }}>
  //             {error}
  //           </Alert>
  //         )}
  //         {message && (
  //           <Alert severity="success" sx={{ mb: 3 }}>
  //             {message}
  //           </Alert>
  //         )}
  //         
  //         <AITeaser 
  //           teaserText={signupData.teaserSummary}
  //           onComplete={handleTeaserComplete}
  //         />
  //       </Box>
  //     </Container>
  //   );
  // }

  // Generate personalized insights when quiz data is available
  useEffect(() => {
    const generateInsights = async () => {
      if (!signupData?.quizAnswers || personalizedInsights || insightsLoading) {
        return;
      }

      // Only generate insights if we came from quiz completion or if explicitly requested
      if (!location.state?.fromQuizCompletion && !location.state?.showPersonalizedInsights) {
        return;
      }

      try {
        setInsightsLoading(true);
        
        // Format quiz answers for RAG processing
        const formattedAnswers = formatQuizForRAG(signupData.quizAnswers);
        
        // Initialize RAG service and get analysis
        const ragService = new AdvancedTherapyRAG();
        const ragAnalysis = await ragService.analyzeQuizAnswers(formattedAnswers);
        
        // Generate structured insights
        const insights = await generateStructuredInsights(signupData.quizAnswers, ragAnalysis);
        
        setPersonalizedInsights(insights);
        setGeneratedInsights(insights);
        
        // Pre-generate audio for instant playback
        console.log('🎵 Pre-generating audio for insights...');
        const audioBlob = await preGenerateInsightsAudio(insights);
        setPreGeneratedAudio(audioBlob);
        
        console.log('Generated personalized insights:', insights);
      } catch (error) {
        console.error('Error generating personalized insights:', error);
        // Fallback to basic insights without RAG
        try {
          const basicInsights = await generateStructuredInsights(signupData.quizAnswers, null);
          setPersonalizedInsights(basicInsights);
          setGeneratedInsights(basicInsights);
          
          // Pre-generate audio for fallback insights too
          const audioBlob = await preGenerateInsightsAudio(basicInsights);
          setPreGeneratedAudio(audioBlob);
        } catch (fallbackError) {
          console.error('Error generating basic insights:', fallbackError);
        }
      } finally {
        setInsightsLoading(false);
      }
    };

    generateInsights();
  }, [signupData, location.state, personalizedInsights, insightsLoading]);

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 2, p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <TestimonialBox sx={{ mb: 3 }}>
          <QuoteText variant="body1" sx={{ mb: 1 }}>
            {testimonial.text}
          </QuoteText>
          <Typography 
            variant="subtitle2" 
            sx={{ textAlign: 'right', color: 'text.secondary', fontStyle: 'italic' }}
          >
            — {testimonial.author}
          </Typography>
        </TestimonialBox>

        {/* Personalized Insights Summary */}
        {(personalizedInsights || insightsLoading) && (
          <Box sx={{
            mb: 3,
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(145deg, #f8f9ff 0%, #e8f2ff 100%)',
            border: '2px solid rgba(33, 150, 243, 0.2)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h5" sx={{ 
                color: '#2196F3', 
                fontWeight: 'bold',
                ...(isPlaying && {
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.02)' },
                    '100%': { transform: 'scale(1)' },
                  },
                }),
              }}>
                🎯 Your Personalized Therapy Plan
              </Typography>
              
              {/* Audio Controls */}
              {audioSupported && personalizedInsights && (
                <Tooltip title={isPlaying ? "Stop listening" : isLoadingAudio ? "Loading audio..." : "Listen to your plan"}>
                  <IconButton
                    onClick={isPlaying ? stopAudio : playInsightsAudio}
                    disabled={isLoadingAudio}
                    sx={{
                      bgcolor: isPlaying ? '#f44336' : isLoadingAudio ? '#9e9e9e' : '#2196F3',
                      color: 'white',
                      '&:hover': {
                        bgcolor: isPlaying ? '#d32f2f' : isLoadingAudio ? '#9e9e9e' : '#1976D2',
                      },
                      '&:disabled': {
                        bgcolor: '#9e9e9e',
                        color: 'white',
                      },
                      boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                      ml: 1
                    }}
                    size="small"
                  >
                    {isLoadingAudio ? (
                      <CircularProgress size={16} sx={{ color: 'white' }} />
                    ) : isPlaying ? (
                      <Pause />
                    ) : (
                      <PlayArrow />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            
            {/* Loading state for insights generation */}
            {insightsLoading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={40} sx={{ color: '#2196F3', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#2196F3', fontWeight: 'bold', mb: 1 }}>
                  Generating Your Personalized Plan...
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                  Our AI is analyzing your responses to create a customized therapy approach just for you.
                </Typography>
              </Box>
            )}
            
            {/* Show insights content only when not loading */}
            {personalizedInsights && !insightsLoading && (
              <>
                {audioSupported && (isPlaying || isLoadingAudio) && (
                  <Typography variant="body2" sx={{ 
                    color: '#666', 
                    textAlign: 'center',
                    mb: 1.5,
                    fontStyle: 'italic'
                  }}>
                    {isLoadingAudio ? 'Preparing audio...' : 'Playing your personalized plan...'}
                  </Typography>
                )}
                
                <Typography variant="body1" sx={{ 
                  mb: 2, 
                  color: '#333',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}>
                  {personalizedInsights.personalizedMessage}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {personalizedInsights.keyInsights?.slice(0, 2).map((insight, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'rgba(255, 255, 255, 0.7)', 
                        borderRadius: 2,
                        height: '100%'
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2196F3', mb: 1 }}>
                          {insight.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                          {insight.content}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Personalized Wellness Plan */}
                {personalizedInsights.personalizedWellnessPlan && personalizedInsights.personalizedWellnessPlan.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ 
                      color: '#4CAF50', 
                      fontWeight: 'bold',
                      mb: 1.5,
                      textAlign: 'center'
                    }}>
                      🌟 Your Personalized Wellness Focus Areas
                    </Typography>
                    <Grid container spacing={1.5}>
                      {personalizedInsights.personalizedWellnessPlan.slice(0, 3).map((plan, index) => (
                        <Grid item xs={12} md={4} key={index}>
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: 'rgba(76, 175, 80, 0.1)', 
                            borderRadius: 2,
                            height: '100%',
                            border: '1px solid rgba(76, 175, 80, 0.2)'
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4CAF50', mb: 0.5, fontSize: '0.8rem' }}>
                              {plan.area}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem' }}>
                              {plan.approach}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {personalizedInsights.urgencyFactors && (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'rgba(255, 193, 7, 0.1)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255, 193, 7, 0.3)' 
                  }}>
                    <Typography variant="subtitle2" sx={{ 
                      color: '#F57C00', 
                      fontWeight: 'bold',
                      mb: 1 
                    }}>
                      ⏰ Why Start Today:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                      {personalizedInsights.urgencyFactors[0]}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
          Choose Your Plan
        </Typography>

        <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
          {plans.map((plan, index) => (
            <Grid item xs={12} md={plan.recommended ? 7 : 5} key={index}>
              <Card 
                sx={{
                  position: 'relative',
                  transform: plan.recommended ? 'scale(1.02)' : 'scale(0.98)',
                  border: plan.recommended ? '3px solid #2196f3' : '1px solid #e0e0e0',
                  boxShadow: plan.recommended 
                    ? '0 12px 40px rgba(33, 150, 243, 0.25), 0 0 0 1px rgba(33, 150, 243, 0.1)' 
                    : '0 2px 8px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease',
                  background: plan.recommended 
                    ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.03) 0%, rgba(255, 255, 255, 1) 100%)'
                    : 'rgba(248, 248, 248, 0.7)',
                  opacity: plan.recommended ? 1 : 0.8,
                  maxHeight: '100vh',
                  overflow: 'visible',
                  '&:hover': {
                    transform: plan.recommended ? 'scale(1.03)' : 'scale(0.99)',
                    boxShadow: plan.recommended 
                      ? '0 16px 50px rgba(33, 150, 243, 0.3), 0 0 0 1px rgba(33, 150, 243, 0.15)' 
                      : '0 4px 12px rgba(0, 0, 0, 0.12)',
                    opacity: plan.recommended ? 1 : 0.9
                  }
                }}
              >
                {plan.recommended && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'linear-gradient(45deg, #ff6b6b 30%, #feca57 90%)',
                      color: 'white',
                      padding: '8px 20px',
                      borderRadius: '20px',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      zIndex: 2,
                      boxShadow: '0 6px 20px rgba(255, 107, 107, 0.4)',
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { transform: 'translateX(-50%) scale(1)' },
                        '50%': { transform: 'translateX(-50%) scale(1.05)' },
                        '100%': { transform: 'translateX(-50%) scale(1)' }
                      }
                    }}
                  >
                    🚀 BEST VALUE
                  </Box>
                )}
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 'bold', 
                      color: plan.recommended ? 'primary.main' : 'text.secondary',
                      fontSize: plan.recommended ? '1.6rem' : '1.4rem',
                      textAlign: 'center',
                      mb: 0.5,
                      mt: plan.recommended ? 1 : 0
                    }}>
                      {plan.name}
                      {plan.recommended && (
                        <Typography component="span" sx={{ 
                          display: 'block',
                          fontSize: '0.8rem',
                          color: '#ff6b6b',
                          fontWeight: 'bold',
                          mt: 0.5
                        }}>
                          Most Popular Choice
                        </Typography>
                      )}
                    </Typography>

                    
                    <Box>
                      <Typography variant="h3" color="primary" sx={{ 
                        fontWeight: 'bold', 
                        mb: 0, 
                        fontSize: plan.recommended ? '2.2rem' : '2rem', 
                        display: 'flex', 
                        alignItems: 'center',
                        lineHeight: 1.2,
                        justifyContent: 'center'
                      }}>
                        {plan.dailyPrice}
                        <Typography component="span" variant="body2" sx={{ 
                          ml: 1, 
                          color: 'text.secondary',
                          fontSize: '0.9rem'
                        }}>
                          per day
                        </Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                        <Typography variant="h6" color="text.primary" sx={{ 
                          fontSize: '1.2rem',
                          fontWeight: 'bold'
                        }}>
                          {plan.price}
                        </Typography>
                        {plan.originalPrice && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              textDecoration: 'line-through',
                              ml: 1,
                              fontSize: '0.9rem'
                            }}
                          >
                            {plan.originalPrice}
                          </Typography>
                        )}
                        {plan.savings && (
                          <Typography
                            variant="body2"
                            sx={{
                              ml: 1,
                              color: '#4caf50',
                              fontWeight: 'bold',
                              fontSize: '0.9rem'
                            }}
                          >
                            {plan.savings}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      {plan.features.map((feature, i) => (
                        <Typography 
                          key={i} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            mb: 1,
                            color: feature.includes('Best value') ? '#2196f3' : 'text.primary',
                            fontWeight: feature.includes('Best value') || plan.recommended ? 'bold' : 'normal',
                            fontSize: '0.9rem',
                            opacity: plan.recommended ? 1 : 0.8
                          }}
                        >
                          <Box 
                            component="span" 
                            sx={{ 
                              mr: 1,
                              color: feature.includes('Best value') ? '#2196f3' : '#4caf50',
                              fontWeight: 'bold',
                              fontSize: '1rem'
                            }}
                          >
                            {plan.recommended ? '✨' : '✓'}
                          </Box>
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                    
                    {/* Subscribe Button */}
                    <Button
                      variant={plan.recommended ? "contained" : "outlined"}
                      color="primary"
                      fullWidth
                      size="large"
                      onClick={() => handleSubscribe(plan.name.toLowerCase())}
                      disabled={loading}
                      sx={{
                        mt: 2,
                        py: 1.5,
                        fontSize: '1rem',
                          fontWeight: 'bold',
                          opacity: plan.recommended ? 1 : 0.8,
                          ...(plan.recommended && {
                            background: 'linear-gradient(45deg, #2196f3 30%, #1976d2 90%)',
                            boxShadow: '0 4px 16px rgba(33, 150, 243, 0.3)',
                            borderRadius: '20px'
                          }),
                          ...(!plan.recommended && {
                            color: 'text.secondary',
                            borderColor: 'rgba(0,0,0,0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.04)'
                            }
                          })
                        }}
                      >
                        {loading ? 'Processing...' : plan.recommended ? 'Get Started Now' : 'Subscribe Monthly'}
                      </Button>
                    
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default SubscriptionPlans;
